import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Link,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';

import {
    type DiagnosisCategory,
    type DiagnosisIssue,
    type DiagnosisResult,
    validateAiSourcePreprocessing,
    validateMockTests,
    validateQuestions,
} from '../admin/adminQuestionDiagnosisApi';

type TabDefinition = { value: DiagnosisCategory | 'All'; label: string; description: string };

const questionTabs: TabDefinition[] = [
    { value: 'MissingVoice', label: 'Missing Voice', description: 'Active voice-required questions without any attached voice.' },
    { value: 'ReadingDropdown', label: 'Dropdown Blanks', description: 'Every enabled dropdown must have 3–5 choices and include its answer.' },
    { value: 'ReadingDragDrop', label: 'Drag & Drop', description: 'Every blank needs an answer and the question needs additional distractor options.' },
];

const mockTabs: TabDefinition[] = [
    { value: 'All', label: 'All Issues', description: 'Mock-test structure and question quality checks for questions used by mock tests.' },
    { value: 'MockTestStructure', label: 'Mock Test Rules', description: 'Sections, groups, counts, task-type matching, duplicates, and active-question checks.' },
    ...questionTabs,
];

const describeImagePreprocessingTabs: TabDefinition[] = [
    { value: 'DescribeImagePreprocessing', label: 'Describe Image', description: 'Active published Describe Image questions with no generated preprocessing artifact.' },
    { value: 'SummarizeGroupDiscussionPreprocessing', label: 'Summarize Group Discussion', description: 'Active published Academic SGD questions with a source script but no generated preprocessing artifact.' },
    { value: 'RetellLecturePreprocessing', label: 'Retell Lecture', description: 'Active published Academic Retell Lecture questions with a source transcript but no generated preprocessing artifact.' },
    { value: 'SummarizeWrittenTextPreprocessing', label: 'Summarize Written Text', description: 'Active published SWT questions with source text but no approved semantic-scoring blueprint.' },
    { value: 'RespondToSituationPreprocessing', label: 'Respond to Situation', description: 'Active published RTS questions with source text but no approved semantic-scoring blueprint.' },
    { value: 'WriteEssayPreprocessing', label: 'Write Essay', description: 'Active published Academic Write Essay questions with source text but no approved semantic-scoring blueprint.' },
    { value: 'WriteEmailPreprocessing', label: 'Write Email', description: 'Active published Core Write Email questions with source text but no approved semantic-scoring blueprint.' },
    { value: 'SummarizeSpokenTextPreprocessing', label: 'Summarize Spoken Text', description: 'Active published SST questions missing a current approved semantic-scoring blueprint.' },
];

function IssueTable({ issues, mockMode }: { issues: DiagnosisIssue[]; mockMode: boolean }) {
    if (issues.length === 0) {
        return <Alert severity="success">No issues found for this validation.</Alert>;
    }

    return (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {mockMode ? <TableCell>Mock test</TableCell> : null}
                        <TableCell>Question</TableCell>
                        <TableCell>Task type</TableCell>
                        <TableCell>Issue</TableCell>
                        <TableCell align="right">Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {issues.map((issue, index) => (
                        <TableRow key={`${issue.mockTestId ?? 'question'}-${issue.examTaskId ?? issue.code}-${issue.code}-${index}`} hover>
                            {mockMode ? (
                                <TableCell sx={{ minWidth: 180 }}>
                                    {issue.mockTestId ? (
                                        <Link component={RouterLink} to={`/mock-tests/${issue.mockTestId}`} underline="hover" fontWeight={700}>
                                            {issue.mockTestTitle || issue.mockTestId}
                                        </Link>
                                    ) : '—'}
                                </TableCell>
                            ) : null}
                            <TableCell sx={{ minWidth: 180 }}>
                                {issue.examTaskId ? (
                                    <Stack spacing={0.25}>
                                        <Link component={RouterLink} to={`/live-exam-task-by-id/${issue.examTaskId}`} underline="hover" fontWeight={700}>
                                            {issue.questionNo || issue.displayTitle || issue.examTaskId}
                                        </Link>
                                        {issue.questionNo && issue.displayTitle ? (
                                            <Typography variant="caption" color="text.secondary">{issue.displayTitle}</Typography>
                                        ) : null}
                                    </Stack>
                                ) : 'Mock-test level'}
                            </TableCell>
                            <TableCell>
                                {issue.examTaskType ? <Chip size="small" label={issue.examTaskType} variant="outlined" /> : '—'}
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" fontWeight={700}>{issue.message}</Typography>
                                <Typography variant="caption" color="text.secondary">{issue.code}{issue.examName ? ` · ${issue.examName}` : ''}</Typography>
                            </TableCell>
                            <TableCell align="right">
                                {issue.examTaskId ? (
                                    <Button component={RouterLink} to={`/live-exam-task-by-id/${issue.examTaskId}`} size="small" variant="outlined">
                                        Edit question
                                    </Button>
                                ) : issue.mockTestId ? (
                                    <Button component={RouterLink} to={`/mock-tests/${issue.mockTestId}`} size="small" variant="outlined">
                                        Open mock test
                                    </Button>
                                ) : null}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export function DiagnosisPage({ mode }: { mode: 'questions' | 'mock-tests' | 'describe-image-preprocessing' }) {
    const mockMode = mode === 'mock-tests';
    const preprocessingMode = mode === 'describe-image-preprocessing';
    const tabs = preprocessingMode ? describeImagePreprocessingTabs : mockMode ? mockTabs : questionTabs;
    const [activeTab, setActiveTab] = useState<TabDefinition['value']>(tabs[0].value);
    const [result, setResult] = useState<DiagnosisResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const runValidation = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setResult(await (preprocessingMode ? validateAiSourcePreprocessing() : mockMode ? validateMockTests() : validateQuestions()));
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Validation failed.');
        } finally {
            setLoading(false);
        }
    }, [mockMode, preprocessingMode]);

    const filteredIssues = useMemo(
        () => (result?.issues ?? []).filter((issue) => activeTab === 'All' || issue.category === activeTab),
        [activeTab, result],
    );
    const activeDefinition = tabs.find((tab) => tab.value === activeTab) ?? tabs[0];

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: 'auto' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} mb={3}>
                <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FactCheckOutlinedIcon color={mockMode ? 'error' : 'primary'} />
                        <Typography variant="h4" fontWeight={800}>{preprocessingMode ? 'AI Source Preprocessing' : mockMode ? 'Mock Test Diagnosis' : 'Question Diagnosis'}</Typography>
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 850 }}>
                        {preprocessingMode
                            ? 'Check every semantic-scoring question for the source or approved preprocessing blueprint required by its scorer, then open each question to resolve it.'
                            : mockMode
                            ? 'Priority validation for paid mock tests. It checks mock-test structure and repeats all supported question checks only for questions included in each mock test.'
                            : 'Validate live question content and jump directly to manual editing for every issue.'}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color={mockMode ? 'error' : 'primary'}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                    onClick={() => void runValidation()}
                    disabled={loading}
                    sx={{ alignSelf: { xs: 'stretch', md: 'center' }, minWidth: 190 }}
                >
                    {loading ? 'Validating…' : result ? 'Refresh validation' : 'Validate now'}
                </Button>
            </Stack>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <Card variant="outlined">
                <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable" scrollButtons="auto" sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}>
                    {tabs.map((tab) => {
                        const count = result?.issues.filter((issue) => tab.value === 'All' || issue.category === tab.value).length;
                        return <Tab key={tab.value} value={tab.value} label={result ? `${tab.label} (${count})` : tab.label} />;
                    })}
                </Tabs>
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1} mb={2}>
                        <Typography color="text.secondary">{activeDefinition.description}</Typography>
                        {result ? (
                            <Typography variant="caption" color="text.secondary">
                                {`Checked ${result.scannedQuestionCount} questions${mockMode ? ` across ${result.scannedMockTestCount} mock tests` : ''} · ${new Date(result.validatedAt).toLocaleString()}`}
                            </Typography>
                        ) : null}
                    </Stack>
                    {result ? <IssueTable issues={filteredIssues} mockMode={mockMode} /> : (
                        <Alert severity="info">Select Validate now to run the checks against the current API database.</Alert>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
